/**
 * 长图「本屏提示词」对客视图(1005 生产验收 P2-7)。
 *
 * 结果页展开「本屏提示词」原来是 ai 发给绘图引擎的**完整合同**:英文风格串、
 * ★开头的系统规则、拼接安全边/禁 logo 等通用要求、首屏词面负向……这些是内部提示,
 * 对客看不懂也不该改。对客只留**这一屏要画什么、印什么字**(ai `compose_screen_prompt`
 * 里「第 N 部分」那几段),并在用户改了它时把系统段原样拼回去再发(ai 侧 promptOverride
 * 逐字用,系统段丢了画幅/安全边/禁 logo 全失效)。
 *
 * 认不出「第 N 部分」结构(逐块路的老提示词)时退回逐行过滤:丢 ★ 规则行、【…】系统段、
 * 纯英文行、禁用词清单行。
 */

const PART_RE = /第\s*[0-9一二三四五六七八九十]+\s*部分/;
/** 内容段之后的系统段起头(compose_screen_prompt 的固定段落)。 */
const TAIL_RE = /^(【[^】]{1,24}】|★|画面上下边缘|要求:|要求：|禁用词|Avoid)/;

export interface ScreenPromptParts {
  /** 系统前段(参考图说明、用户整体要求、整屏版式结构句)—— 不展示,改写时原样拼回。 */
  head: string;
  /** 对客可见可改的「第 N 部分」内容段。 */
  body: string;
  /** 系统后段(英文风格串、★规则、通用要求)—— 不展示,改写时原样拼回。 */
  tail: string;
}

function isInternalLine(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  if (TAIL_RE.test(t)) return true;
  if (/^【[^】]{1,20}】/.test(t)) return true; // 【画幅】【画质】【参考图】等系统段
  if (/禁用词|禁止使用以下|不得出现以下词/.test(t)) return true;
  // 纯英文风格串(中文字符占比 < 10% 且够长)
  const cjk = (t.match(/[一-鿿]/g) ?? []).length;
  return t.length >= 24 && cjk / t.length < 0.1;
}

export function splitScreenPrompt(raw: string | null | undefined): ScreenPromptParts {
  const text = (raw ?? "").trim();
  if (!text) return { head: "", body: "", tail: "" };
  const lines = text.split("\n");
  const first = lines.findIndex((l) => PART_RE.test(l) && /^\s*第/.test(l));
  if (first >= 0) {
    let end = lines.length;
    for (let i = first + 1; i < lines.length; i++) {
      if (TAIL_RE.test(lines[i].trim()) || /^【[^】]{1,20}】/.test(lines[i].trim())) {
        end = i;
        break;
      }
    }
    return {
      head: lines.slice(0, first).join("\n"),
      body: lines.slice(first, end).join("\n").trim(),
      tail: lines.slice(end).join("\n"),
    };
  }
  // 老结构:逐行过滤,不可逆 —— head/tail 留空,改写时由调用方决定是否允许。
  return {
    head: "",
    body: lines
      .filter((l) => !isInternalLine(l))
      .join("\n")
      .trim(),
    tail: "",
  };
}

/** 对客展示/编辑用的那段文本。 */
export function customerPromptText(raw: string | null | undefined): string {
  return splitScreenPrompt(raw).body;
}

/** 认得出结构 = 能把用户改过的内容段与系统段安全拼回。 */
export function canRewrapPrompt(raw: string | null | undefined): boolean {
  const p = splitScreenPrompt(raw);
  return !!p.body && (!!p.head || !!p.tail);
}

/** 用户改过的内容段 + 原系统段 → 完整提示词(发 promptOverride)。 */
export function rewrapPrompt(raw: string | null | undefined, editedBody: string): string {
  const p = splitScreenPrompt(raw);
  return [p.head, editedBody.trim(), p.tail].filter((s) => s.trim()).join("\n");
}

/** 内容段里夹着的指令括注:(逐字照搬,不许改写/增删/换算)这一类。 */
const INSTRUCTION_PAREN_RE =
  /[（(][^（()）]*(?:照搬|不许|不得|禁止|增删|换算|印出来)[^（()）]*[）)]/g;
/** 内容段里的指令分句(按 ;；。 切开后命中即丢)。 */
const INSTRUCTION_CLAUSE_RE = /不许|不得|禁止|逐字照搬|必须完整呈现|印出来|绝对主角/;
const PART_HEAD_RE = /^(第\s*[0-9一二三四五六七八九十]+\s*部分)(「[^」]*」)?\s*(?:——\s*(.*))?$/;

function cleanClauses(text: string): string {
  return text
    .replace(INSTRUCTION_PAREN_RE, "")
    .split(/(?<=[;；。])/)
    .filter((c) => !INSTRUCTION_CLAUSE_RE.test(c))
    .join("")
    .replace(/[;；。:：,，\s]+$/, "")
    .trim();
}

/**
 * 对客「只读展示」用的本屏内容(1006 演示机复测:一键长图任务详情分屏列表露出
 * 「本屏内容:第1部分 —— 痛点/卖点大字排版画面,文案是绝对主角。必须完整呈现的内容(逐字照搬,
 * 不许改写/增删/换算):…」)。在 `customerPromptText`(只留「第 N 部分」内容段)之上再去掉
 * 段头里的版式提示与「必须/不许」类指令,只剩「第 N 部分「标题」」+ 画面/文字 + 逐条内容。
 * ★只用于展示;改内容仍走 `customerPromptText` + `rewrapPrompt`(要逐字拼回)。
 */
export function customerScreenContent(raw: string | null | undefined): string {
  const body = customerPromptText(raw);
  if (!body) return "";
  const out: string[] = [];
  for (const line of body.split("\n")) {
    const t = line.trim();
    if (!t) continue;
    const head = t.match(PART_HEAD_RE);
    if (head) {
      const title = `${head[1].replace(/\s+/g, "")}${head[2] ?? ""}`;
      const rest = head[3] ?? "";
      const visual = rest.match(/主视觉画面[:：]\s*([^;；]*)/);
      const words = rest.match(/必须逐字印出[:：]\s*(.*)$/);
      const parts = [title];
      const desc = visual ? cleanClauses(visual[1]) : "";
      if (desc) parts.push(`画面:${desc}`);
      if (words && words[1].trim()) parts.push(`文字:${words[1].trim()}`);
      out.push(parts.join(" · "));
      continue;
    }
    const req = t.match(/^本部分的用户要求[^:：]*[:：]\s*(.*)$/);
    if (req) {
      if (req[1].trim()) out.push(`本部分要求:${req[1].trim()}`);
      continue;
    }
    const cleaned = t.startsWith("-")
      ? t.replace(INSTRUCTION_PAREN_RE, "").trim()
      : cleanClauses(t);
    if (cleaned && cleaned !== "-") out.push(cleaned);
  }
  return out.join("\n");
}
